<template>
  <div>
    <section>
      <section v-if="paymentMethod==='stripe' && lastPaymentMethod === 'stripe'" class="w-full">
        <section v-if="!seeMoreCards">
          <ul class="mt-1" v-if="cards.length>0">
            <li v-if="lastCard"
              class="pt-2 flex justify-start items-center">
                <div class="flex flex-col">
                  <card-logo-and-numbers :card="lastCard" class="w-full pl-2" />
                </div>
            </li>
            <span class="w-full text-sm" v-if="lastCard">({{ $t('cards.last') }})</span>
          </ul>
          <div class="flex justify-end pt-4">
            <button class="flex items-center text-sm hover:text-b2c-red-300"
              v-if="cards.length>0" @click="seeMoreCards = true; addNewCard = false;">
                {{ $t('cards.seeAll') }}
              <dropdown-arrow class="ml-2" />
            </button>
          </div>
          <div class="flex justify-between items-center mt-6">
            <button class="p-2 px-6 rounded-md shadow-md hover:bg-b2c-red-300 hover:text-white"
              @click="seeMoreCards = true; addNewCard = true">
                {{$t('stripe.add')}}
            </button>
          </div>
        </section>
        <section class="flex justify-end" v-if="seeMoreCards || lastPaymentMethod !== 'stripe'">
          <button class="flex items-center text-sm hover:text-b2c-red-300 pt-10"
            v-if="cards.length>0 && showCloseCards" @click="seeMoreCards = false">
              {{ $t('cards.close') }}
            <dropup-arrow class="ml-2" />
          </button>
        </section>
      </section>
      <section v-if="paymentMethod==='sepa' && lastPaymentMethod === 'sepa'" class="w-full">
        <section v-if="!seeMoreIbans">
          <ul class="mt-1">
            <li v-if="lastIban"
              class="pt-2 flex justify-start items-center">
                <div class="flex flex-col w-full">
                  <iban :iban="lastIban" class="w-full pl-2" :notShowBin="true" />
                </div>
            </li>
            <span class="w-full text-sm">({{ $t('cards.last') }})</span>
          </ul>
          <div class="flex justify-end pt-4">
            <button class="flex items-center text-sm hover:text-b2c-red-300"
              v-if="ibans.length>0 && !addNewIban" @click="seeMoreIbans = true; addNewIban = false;">
                {{ $t('iban.seeAll') }}
              <dropdown-arrow class="ml-2" />
            </button>
          </div>
          <div class="flex justify-between items-center mt-6">
            <button class="p-2 px-6 rounded-md shadow-md hover:bg-b2c-red-300 hover:text-white text-xs md:text-md"
              @click="seeMoreIbans = true; addNewIban = true;">
                {{ $t('iban.add') }}
            </button>
          </div>
        </section>
        <section class="flex justify-end" v-if="seeMoreIbans || lastPaymentMethod !== 'sepa'">
          <button class="flex items-center text-sm hover:text-b2c-red-300 pt-10"
            v-if="ibans.length>0 && showCloseIban" @click="seeMoreIbans = false">
              {{ $t('cards.close') }}
            <dropup-arrow class="ml-2" />
          </button>
        </section>
      </section>
  </section>
  <section v-if="paymentMethod==='stripe' && (seeMoreCards || lastPaymentMethod!=='stripe')" class="mb-20">
    <cards-list :radioButtons="true" v-model="stripePaymentMethod" :add="addNewCard" />
  </section>
  <section v-if="paymentMethod==='sepa' && (seeMoreIbans || lastPaymentMethod!=='sepa')" class="mb-20">
    <iban-list :radioButtons="true" v-model="stripePaymentMethod" :add="addNewIban" />
  </section>
  </div>
</template>

<script>
import {
  getCustomerCards, getCustomerIbans, getCustomerDefaultPaymentMethod, getCustomerSetupIntent,
} from '@/services/OrdersService';
import { mapGetters } from 'vuex';
import CardsList from './profile/CardsList.vue';
import DropupArrow from './graphics/DropupArrow.vue';
import IbanList from './profile/IbanList.vue';
import DropdownArrow from './graphics/DropdownArrow.vue';
import Iban from './graphics/Iban.vue';
import CardLogoAndNumbers from './graphics/CardLogoAndNumbers.vue';

export default {
  props: ['paymentMethod', 'value'],

  data() {
    return {
      cards: [],
      lastCard: null,
      addNewCard: false,
      seeMoreCards: false,

      ibans: [],
      lastIban: null,
      addNewIban: false,
      seeMoreIbans: false,

      selectedPaymentMethod: 'last',
      stripePaymentMethod: this.value,
      stripeDefault: null,
      intent: null,
      lastPaymentMethod: '',

      showCloseCards: true,
      showCloseIban: true,

      isLoading: false,
    };
  },

  computed: {
    ...mapGetters({
      customer: ['auth/customer'],
      user: ['auth/user'],
      deletedPaymentMethod: ['cart/deletedPaymentMethod'],
    }),
  },

  components: {
    CardsList,
    DropupArrow,
    IbanList,
    DropdownArrow,
    Iban,
    CardLogoAndNumbers,
  },

  watch: {
    stripePaymentMethod() {
      this.$emit('input', this.stripePaymentMethod);
    },
    paymentMethod() {
      this.stripePaymentMethod = 'last';
      if (!this.paymentMethod || !this.$store.getters['cart/paymentMethod']) {
        return;
      }
      if (this.paymentMethod === this.$store.getters['cart/paymentMethod'].slug) {
        this.setStripeDefaultPaymentMethod();
      }
      this.seeMoreCards = false;
      this.seeMoreIbans = false;
    },
    addNewCard() {
      if (this.addNewCard === true) {
        getCustomerSetupIntent(this.customer.id).then((response) => {
          this.intent = response.data.intent;
        });
      }
    },
    seeMoreCards() {
      this.showCloseCards = false;
    },
    seeMoreIbans() {
      this.showCloseIban = false;
    },
  },

  mounted() {
    this.getLastPaymentMethod();
    this.checkStripePaymentMethod();

    if (this.paymentMethod === 'stripe' && this.customer.card_last_four) {
      this.seeMoreIbans = true;
      this.lastPaymentMethod = 'stripe';
    }
    if (this.paymentMethod === 'sepa' && this.customer.iban_last_four) {
      this.seeMoreCards = true;
      this.lastPaymentMethod = 'sepa';
    }

    this.getCards();
    this.getIbans();
  },

  methods: {
    getLastPaymentMethod() {
      if (this.customer.card_last_four) {
        this.lastCard = {
          card_brand: this.customer.card_brand,
          card_last_four: this.customer.card_last_four,
        };
      }
      if (this.customer.iban_last_four) {
        this.lastIban = {
          iban_last_four: this.customer.iban_last_four,
        };
      }
    },

    getCards() {
      getCustomerCards(this.customer.id).then((response) => {
        this.cards = response.data;
      }).catch(() => {
        this.createNotification('error', `${this.$t('notifications.errorReload')}`);
      });
    },

    getIbans() {
      getCustomerIbans(this.customer.id).then((response) => {
        this.ibans = response.data;
      }).catch(() => {
        this.createNotification('error', `${this.$t('notifications.errorReload')}`);
      });
    },

    checkStripePaymentMethod() {
      getCustomerDefaultPaymentMethod(this.customer.id).then((response) => {
        this.stripeDefault = response.data ? response.data.id : null;
        this.setStripeDefaultPaymentMethod();
      }).catch((error) => {
        this.checkRoutingAuthentication(error);
      });
    },

    setStripeDefaultPaymentMethod() {
      this.stripePaymentMethod = this.stripeDefault;
      this.selectedPaymentMethod = this.stripePaymentMethod;
    },

  },

};
</script>
